"use client";

import { useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";
import type { Testimonial } from "@/lib/siteContent";
import type { PageTexts } from "@/lib/siteTexts";

export default function TestimonialsSection({ testimonials, texts }: { testimonials: Testimonial[]; texts: PageTexts<"homepage"> }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Nothing to show without testimonials
  if (testimonials.length === 0) {
    return null;
  }

  return (
    <section className="py-28 bg-[#0D1526] relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-15" />
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <span className="section-label">{texts["reviews.label"]}</span>
            <h2 className="font-display font-bold text-4xl lg:text-5xl text-white mt-3">
              {texts["reviews.title"]}{" "}
              <span className="gradient-text">{texts["reviews.titleAccent"]}</span>
            </h2>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => scrollRef?.current?.scrollBy({ left: -440, behavior: "smooth" })}
              className="w-10 h-10 rounded-xl glass border border-[rgba(79,124,255,0.2)] flex items-center justify-center text-[#4A6080] hover:text-[#00D4FF] hover:border-[rgba(0,212,255,0.3)] transition-all">
              <Icon name="ChevronLeftIcon" size={18} />
            </button>
            <button
              onClick={() => scrollRef?.current?.scrollBy({ left: 440, behavior: "smooth" })}
              className="w-10 h-10 rounded-xl glass border border-[rgba(79,124,255,0.2)] flex items-center justify-center text-[#4A6080] hover:text-[#00D4FF] hover:border-[rgba(0,212,255,0.3)] transition-all">
              <Icon name="ChevronRightIcon" size={18} />
            </button>
          </div>
        </div>

        {/* Scroll container */}
        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-auto no-scrollbar pb-4"
          style={{ scrollSnapType: "x mandatory" }}>
          {testimonials.map((t) => (
            <div
              key={t.id}
              className="flex-shrink-0 w-[380px] glass rounded-2xl p-8 card-hover border border-[rgba(79,124,255,0.12)]"
              style={{ scrollSnapAlign: "start" }}>
              {/* Stars */}
              <div className="flex gap-1 mb-5">
                {Array.from({ length: t.rating }).map((_, i) => (
                  <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="#FFB800">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
              </div>

              {/* Quote */}
              <blockquote className="text-[#8BA3CC] text-sm leading-relaxed mb-6">
                &ldquo;{t.content}&rdquo;
              </blockquote>

              {/* Author */}
              <div className="flex items-center gap-3">
                {t.author_avatar ? (
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                    <AppImage
                      src={t.author_avatar}
                      alt={`Фото ${t.author_name}`}
                      width={40}
                      height={40}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[rgba(79,124,255,0.2)] flex items-center justify-center flex-shrink-0">
                    <span className="text-[#4F7CFF] text-sm font-bold">
                      {t.author_name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <div className="text-white text-sm font-semibold">{t.author_name}</div>
                  <div className="text-[#4A6080] text-xs">
                    {[t.author_position, t.author_company].filter(Boolean).join(", ")}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}